import {
    type ProductVariantInventory,
    createProductVariantInventoryKey,
} from '@libs/algolia';
import { DeliveryType, getLocale, getWebInventory } from '@libs/core';
import apiProxyClient from '@libs/api-proxy-client';

/**
 * Need to explicitly convert to Number because we are sending
 * store with id xyz, but get 0xyz in response.
 *
 * Probably there is a bug somewhere, where leading zeroes are removed from store id
 */
export function getTypeDelivery(
    storeId: string,
    bopis = '',
    sdd = ''
): DeliveryType | '' {
    switch (Number(storeId)) {
        case Number(bopis):
            return DeliveryType.BOPIS;
        case Number(sdd):
            return DeliveryType.SDD;
        default:
            return '';
    }
}

type FetchInventoryList = {
    productIds: string[];
    bopis?: string;
    sdd?: string;
};

type StoreSku = {
    store: string;
    sku: string;
};

/*
 * Fetch function should only make queries and return the result,
 * all the logic of data preparation before the query
 * and after the query should be placed in separate files.
 *
 * Needs to be fixed.
 */

export const fetchInventoryList = async ({
    productIds,
    bopis,
    sdd,
}: FetchInventoryList): Promise<ProductVariantInventory | undefined> => {
    const locale = getLocale();
    const webStoreId = getWebInventory(locale);
    const deliveryTypes = [webStoreId, bopis, sdd];

    const storeSkus = productIds.reduce<StoreSku[]>(
        (storeSkuCollection, productId) => {
            deliveryTypes.forEach(store => {
                if (store) {
                    storeSkuCollection.push({
                        sku: productId,
                        store,
                    });
                }
            });

            return storeSkuCollection;
        },
        []
    );

    let fetchedInventories;
    try {
        fetchedInventories =
            await apiProxyClient.inventory.getProductInventoryPairs(storeSkus);
    } catch {
        // Leaving catch not to break any existing code. error response was not used anywhere
        return undefined;
    }

    return fetchedInventories.reduce<ProductVariantInventory>(
        (productInventory, inventory) => {
            const { sku, ...storeIds } = inventory;

            Object.entries(storeIds).forEach(([storeId, quantity]) => {
                const numQuantity = Number(quantity);
                // Force add SDD type in case BOPIS === SDD (and apply that only in the right storeID)
                if (
                    Number(storeId) === Number(bopis) &&
                    Number(storeId) === Number(sdd)
                ) {
                    const deliveryType = getTypeDelivery(
                        storeId,
                        undefined,
                        sdd
                    );
                    const inventoryKey = createProductVariantInventoryKey(
                        sku,
                        deliveryType
                    );

                    productInventory[inventoryKey] = {
                        id: storeId,
                        ats: numQuantity,
                        orderable: numQuantity > 0,
                    };
                }

                const deliveryType = getTypeDelivery(storeId, bopis, sdd);
                const inventoryKey = createProductVariantInventoryKey(
                    sku,
                    deliveryType
                );

                productInventory[inventoryKey] = {
                    id: storeId,
                    ats: numQuantity,
                    orderable: numQuantity > 0,
                };
            });

            return productInventory;
        },
        {}
    );
};
